import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CWFieldInput from '../../../../../components/custom-worksheet/CWFieldInput';
import CWInputControlWrapper from '../../../../../components/custom-worksheet/CWInputControlWrapper';
import { NUMERIC_TYPES } from '../../../../../constants';
import { CWTSchemaPropertySummary, TableRowStyles, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { selectCurrentEditingField } from '../../store/selectors';
import { editField, setCurrentEditingField } from '../../store/slice';
import CWReadField from '../field/CWReadField';

type TableFieldProps = {
    property: (WorksheetSchemaField & { styles: TableRowStyles }) | (CWTSchemaPropertySummary & { styles: TableRowStyles });
    inGenerated: boolean;
};

function TableField({ property, inGenerated }: TableFieldProps): JSX.Element {
    const ref = useRef<HTMLDivElement>(null);

    const [value, setValue] = useState(property.value);

    const dispatch = useDispatch();
    const currentEditingField = useSelector(selectCurrentEditingField);
    const isEditing = currentEditingField === property.id;
    const readOnly = property.calculation || ('readOnly' in property ? property.readOnly : false);

    useEffect(() => {
        if (isEditing && ref.current) {
            const inputs = ref.current?.getElementsByTagName('input') ?? [];

            for (let i = 0; i < inputs.length; i++) {
                const input = inputs[i];
                input.size = 1;
            }
        }
    }, [isEditing]);

    /**
     * If resetEditingField is true, setEditField(undefined) (this will clear bolded nav item and hide indigo side border)
     * If resetEditingField is false, don't setEditField(undefined) (this will allow sliding animation when moving from one nav item to another)
     */
    function handleSave(resetEditingField = true): void {
        let updatedFieldValue = value;

        if (updatedFieldValue === '' || updatedFieldValue === null) {
            updatedFieldValue = null;
        }

        if (NUMERIC_TYPES.includes(property.type) && (value === '.' || value === '-')) {
            if (resetEditingField) {
                dispatch(setCurrentEditingField(undefined));
            }
            return;
        }

        // If value entered is greater than the MAX_SAFE_INTEGER, don't save the value
        if (typeof value === 'number' && value > Number.MAX_SAFE_INTEGER) {
            return;
        }

        if (updatedFieldValue !== undefined) {
            dispatch(editField({ id: property.id, details: { value: updatedFieldValue } }));
        }

        if (resetEditingField) {
            dispatch(setCurrentEditingField(undefined));
        }
    }
    return (
        <div
            className="group outline-none"
            style={{ minWidth: property.styles.width }}
            onFocus={
                readOnly
                    ? undefined
                    : (): void => {
                          dispatch(setCurrentEditingField(property.id));
                      }
            }
            tabIndex={readOnly ? undefined : 0}
        >
            <div
                className="min-h-[38px] group items-center cursor-text flex py-1 px-4  group-focus-within:ring-indigo-600  group-focus-within:ring-2  group-focus-within:rounded  group-focus-within:ring-inset outline-none [&_div]:bg-transparent [&_div]:hover:bg-transparent [&_div]:group-focus-within:outline-none [&_div]:group-focus-within:ring-0 [&_div]:p-0 [&_p]:line-clamp-none [&_p]:break-words [&_p]:whitespace-normal [&_div]:max-w-[200px] [&_div]:border-0"
                style={{ justifyContent: property.styles.alignment }}
                ref={ref}
            >
                {isEditing ? (
                    <CWInputControlWrapper
                        hideButtons
                        zIndex={1}
                        onSave={handleSave}
                        onCancel={(): void => {
                            dispatch(setCurrentEditingField(undefined));
                        }}
                    >
                        <CWFieldInput
                            autoFocus
                            fieldType={property.type}
                            onValueChange={setValue}
                            defaultValue={property.value}
                            showIcon={false}
                            textAlign={property.styles.alignment}
                        />
                    </CWInputControlWrapper>
                ) : (
                    <div className="items-center flex flex-wrap gap-1">
                        <CWReadField field={property} showIcon={false} alignment={property.styles.alignment} mode={'row'}></CWReadField>
                        {'helperText' in property && property.helperText && inGenerated && (
                            <label className="text-xs break-words whitespace-normal text-gray-600">{property.helperText}</label>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TableField;
